import {action, makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {flatMap, get} from "lodash";

export interface ISquadPrediction {
	squadId: number;
	matchId: number;
}

export type TPredictions = ISquadPrediction[];

export type TPredictionsObj = Record<number, TPredictions>;

export interface IHeavyPredictionsStore {
	readonly i18n: ILocalizationStore;

	get predictions(): TPredictionsObj;
	get predictionBySelectedRoundId(): TPredictions;
	get hasPredictions(): boolean;
	get isPredictionsSaved(): boolean;
	get isPredictionsChanged(): boolean;

	predictionByRoundId(roundId: number): TPredictions;
	setPredictionsForRound(squadObj: ISquadPrediction): void;
	resetPredictionForRound(): void;
	setPredictionsFromLocalStorage(): void;
	setAutoFillPrediction(prediction: TPredictions): void;
	setIsPredictionsSaved(value: boolean): void;
	setIsPredictionsChanged(value: boolean): void;
}

@injectable()
export class HeavyPredictionsStore implements IHeavyPredictionsStore {
	@observable private _predictions: TPredictionsObj = {};
	@observable private _isPredictionsSaved: boolean = false;
	@observable private _isPredictionsChanged: boolean = false;

	get predictions() {
		return this._predictions;
	}

	get predictionBySelectedRoundId(): TPredictions {
		return get(this._predictions, this._roundStore.selectedRoundId, [] as TPredictions);
	}

	get hasPredictions() {
		return flatMap(this._predictions).length > 0;
	}

	get isPredictionsSaved() {
		return this._isPredictionsSaved;
	}

	get isPredictionsChanged() {
		return this._isPredictionsChanged;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	predictionByRoundId = (roundId: number) => {
		return get(this._predictions, roundId, [] as TPredictions);
	};

	@action setIsPredictionsSaved(value: boolean) {
		this._isPredictionsSaved = value;
	}

	@action setIsPredictionsChanged(value: boolean) {
		this._isPredictionsChanged = value;
	}

	@action setPredictionsForRound(squadObj: ISquadPrediction) {
		const currentRoundId = this._roundStore.selectedRoundId;
		const roundPredictions = get(this._predictions, currentRoundId, []);

		const hasPrediction = roundPredictions.find((el) => el.matchId === squadObj.matchId);
		const currentRoundPrediction = hasPrediction
			? roundPredictions.filter((el) => el.matchId !== squadObj.matchId)
			: roundPredictions;

		this._predictions = {
			...this._predictions,
			[currentRoundId]: [...currentRoundPrediction, squadObj],
		};

		this.setPredictionToLocalStorage(this.predictions);
	}

	@action resetPredictionForRound() {
		const currentRoundId = this._roundStore.selectedRoundId;

		this._predictions = {
			...this._predictions,
			[currentRoundId]: [],
		};

		this.setPredictionToLocalStorage(this.predictions);
	}

	@action setPredictionsFromLocalStorage() {
		const predictions = JSON.parse(
			localStorage.getItem("predictions") || "{}"
		) as TPredictionsObj;

		if (predictions) {
			this._predictions = predictions;
		}
	}

	@action setAutoFillPrediction(prediction: TPredictions) {
		this._predictions = {
			...this._predictions,
			[this._roundStore.selectedRoundId]: prediction,
		};
		this.setPredictionToLocalStorage(this.predictions);
	}

	private setPredictionToLocalStorage = (predictions: TPredictionsObj) => {
		localStorage.removeItem("predictions");
		localStorage.setItem("predictions", JSON.stringify(predictions));
	};
}
